import React, { useState, useEffect, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { getStaff, getPresenceToday, updateProfile } from '../api/apiClient';
import socket from '../socket';
import './StaffDirectory.css';

// Render the user-chosen crop area to a square JPEG data URI (mirrors Account Settings).
const getCroppedImg = (imageSrc, pixelCrop) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const size = Math.min(pixelCrop.width, pixelCrop.height, 400);
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.88));
    };
    image.src = imageSrc;
  });

const CATEGORIES = [
  { key: 'Attorneys', label: 'Attorneys' },
  { key: 'Staff',     label: 'Staff' },
];

const ATTORNEY_ROLES = new Set(['Partner', 'Of Counsel/Senior Counsel', 'Associate']);

// Leadership pinned to the top of their section (COO leads the Staff section).
const PINNED_FIRST = ['Christopher J. Esbrook', 'Esther Yahnig', 'Ginger Wiley'];
const lastName  = name => name.trim().split(/\s+/).pop().toLowerCase();
const sortByLast = arr => [...arr].sort((a, b) => {
  const pa = PINNED_FIRST.indexOf(a.name);
  const pb = PINNED_FIRST.indexOf(b.name);
  if (pa !== -1 || pb !== -1) {
    return (pa === -1 ? 999 : pa) - (pb === -1 ? 999 : pb);
  }
  return lastName(a.name).localeCompare(lastName(b.name));
});

// Order attorneys by title: Founding → Managing → Partner → Senior Counsel → Associate.
const TITLE_RANK = {
  'founding partner': 1, 'managing partner': 2, 'partner': 3,
  'senior counsel': 4, 'of counsel': 4, 'associate': 5,
};
const attorneyRank = m => TITLE_RANK[(m.title || '').trim().toLowerCase()] ?? 6;
const sortAttorneys = arr => [...arr].sort((a, b) =>
  attorneyRank(a) - attorneyRank(b) || lastName(a.name).localeCompare(lastName(b.name)));
const initials  = name => name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
const fmtPhone  = (phone, ext) => phone ? (ext ? `${phone} ext. ${ext}` : phone) : null;

const BIRTH_MONTH_FULL = { '01':'January','02':'February','03':'March','04':'April','05':'May','06':'June','07':'July','08':'August','09':'September','10':'October','11':'November','12':'December' };
const fmtBirthday = (b) => { const [m, d] = (b || '').split('-'); return (BIRTH_MONTH_FULL[m] && d) ? `${BIRTH_MONTH_FULL[m]} ${parseInt(d, 10)}` : null; };

const TeamsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Right smaller person (behind, darker) */}
    <circle cx="19" cy="6" r="2.5" fill="#464EB8"/>
    <path d="M15.5 17c0-2 1.57-3.5 3.5-3.5s3.5 1.5 3.5 3.5z" fill="#464EB8"/>
    {/* Left larger person (lighter, partially behind badge) */}
    <circle cx="15.5" cy="5" r="3.5" fill="#7B83EB"/>
    <path d="M10.5 17c0-2.76 2.24-5 5-5s5 2.24 5 5z" fill="#7B83EB"/>
    {/* Teams badge */}
    <rect x="1" y="6" width="14" height="14" rx="3" fill="#5059C9"/>
    {/* T letter */}
    <path d="M4 10.5h8M8 10.5V17" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="14" height="10" rx="2"/>
    <path d="M1 3l7 5 7-5"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 11.3l-2.8-1.2a1 1 0 0 0-1.1.23l-1.2 1.47A11 11 0 0 1 4.2 6.6L5.67 5.4a1 1 0 0 0 .23-1.1L4.7 1.5A1 1 0 0 0 3.6.93L1.4 1.5A1 1 0 0 0 .75 2.6 13.5 13.5 0 0 0 13.4 15.25a1 1 0 0 0 1.1-.65l.57-2.2a1 1 0 0 0-.57-1.1z"/>
  </svg>
);

const MobileIcon = () => (
  <svg width="10" height="12" viewBox="0 0 10 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="1" width="8" height="14" rx="2"/>
    <line x1="5" y1="12" x2="5" y2="12" strokeWidth="2"/>
  </svg>
);

const CakeIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 14h12v-4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path d="M2 11h12"/>
    <line x1="5.5" y1="4.5" x2="5.5" y2="6"/><line x1="8" y1="4.5" x2="8" y2="6"/><line x1="10.5" y1="4.5" x2="10.5" y2="6"/>
    <circle cx="5.5" cy="3.4" r="0.5"/><circle cx="8" cy="3.4" r="0.5"/><circle cx="10.5" cy="3.4" r="0.5"/>
  </svg>
);

// ── Expanded profile modal ────────────────────────────────────────────────────
const EDIT_CATEGORIES = ['Partner', 'Of Counsel/Senior Counsel', 'Associate', 'Staff'];
const EDIT_OFFICES = ['Chicago', 'Delaware', 'Washington D.C.'];
const BIRTH_MONTHS = [
  ['01', 'Jan'], ['02', 'Feb'], ['03', 'Mar'], ['04', 'Apr'], ['05', 'May'], ['06', 'Jun'],
  ['07', 'Jul'], ['08', 'Aug'], ['09', 'Sep'], ['10', 'Oct'], ['11', 'Nov'], ['12', 'Dec'],
];

const ProfileModal = ({ member, isAdmin, onClose, onSaved }) => {
  const ref     = useRef(null);
  const menuRef = useRef(null);
  const [data, setData]       = useState(member);
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenu]   = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState({});
  const [avatarData, setAvatarData]     = useState(null);
  const [avatarChanged, setAvatarChanged] = useState(false);
  const [cropSrc, setCropSrc]           = useState(null);
  const [crop, setCrop]                 = useState({ x: 0, y: 0 });
  const [zoom, setZoom]                 = useState(1);
  const [cropPixels, setCropPixels]     = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { setData(member); setEditing(false); setMenu(false); setCropSrc(null); }, [member]);

  useEffect(() => {
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false);
      // don't close the whole modal on an outside click while editing (avoid losing changes)
      if (!editing && ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose, editing]);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') { editing ? setEditing(false) : onClose(); } };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, editing]);

  const startEdit = () => {
    const [bm, bd] = (data.birthday || '').split('-');
    setForm({
      name: data.name || '', role_category: data.role_category || 'Staff',
      title: data.title || '', office: data.office || 'Chicago',
      phone: data.phone || '', phone_extension: data.phone_extension || '',
      mobile_phone: data.mobile_phone || '', bio: data.bio || '',
      birthMonth: bm || '', birthDay: bd || '',
    });
    setAvatarData(data.avatar || null); setAvatarChanged(false);
    setEditing(true); setMenu(false);
  };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickAvatar = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10 MB.'); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = ev => { setCropSrc(ev.target.result); setCrop({ x: 0, y: 0 }); setZoom(1); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const applyCrop = useCallback(async () => {
    if (!cropPixels) return;
    const uri = await getCroppedImg(cropSrc, cropPixels);
    setAvatarData(uri); setAvatarChanged(true); setCropSrc(null);
  }, [cropSrc, cropPixels]);
  const removeAvatar = () => { setAvatarData(null); setAvatarChanged(true); };

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    const birthday = form.birthMonth && form.birthDay
      ? `${form.birthMonth}-${String(form.birthDay).padStart(2, '0')}` : null;
    const payload = {
      name: form.name.trim(), role_category: form.role_category, title: form.title.trim(),
      office: form.office, phone: form.phone.trim(), phone_extension: form.phone_extension.trim(),
      mobile_phone: form.mobile_phone.trim(), bio: form.bio.trim(), birthday,
      // avatarData carries the (possibly newly cropped) photo; unchanged = existing preserved
      avatar: avatarData ?? null, avatar_changed: avatarChanged,
    };
    try {
      await updateProfile(data.id, payload);
      setData(d => ({ ...d, ...payload }));
      setEditing(false);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save profile');
    } finally { setSaving(false); }
  };

  const office = fmtPhone(data.phone, data.phone_extension);

  return (
    <div className="sd-modal-overlay">
      <div className="sd-modal" ref={ref}>
        {isAdmin && !editing && (
          <div className="sd-modal-menu-wrap" ref={menuRef}>
            <button className="sd-modal-menu-btn" onClick={() => setMenu(o => !o)} title="Actions">⋯</button>
            {menuOpen && (
              <div className="sd-modal-menu">
                <button onClick={startEdit}>Edit profile</button>
              </div>
            )}
          </div>
        )}
        <button className="sd-modal-close" onClick={onClose}>✕</button>

        {cropSrc && (
          <div className="crop-overlay sd-crop-overlay">
            <div className="crop-modal">
              <p className="crop-title">Drag &amp; scroll to crop the photo</p>
              <div className="crop-area">
                <Cropper
                  image={cropSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, pixels) => setCropPixels(pixels)}
                />
              </div>
              <div className="crop-zoom-row">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                <input className="crop-zoom-slider" type="range" min={1} max={3} step={0.01} value={zoom} onChange={e => setZoom(Number(e.target.value))} />
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
              </div>
              <div className="crop-actions">
                <button className="crop-cancel-btn" onClick={() => setCropSrc(null)}>Cancel</button>
                <button className="crop-confirm-btn" onClick={applyCrop}>Apply</button>
              </div>
            </div>
          </div>
        )}

        {editing ? (
          <div className="sd-edit">
            <h2 className="sd-edit-heading">Edit Profile</h2>

            <div className="sd-edit-avatar-row">
              <div className="sd-edit-avatar">
                {avatarData
                  ? <img src={avatarData} alt="" />
                  : <span>{initials(form.name || data.name)}</span>}
              </div>
              <div className="sd-edit-avatar-actions">
                <button type="button" className="sd-edit-avatar-btn" onClick={() => fileRef.current?.click()}>
                  {avatarData ? 'Change photo' : 'Upload photo'}
                </button>
                {avatarData && (
                  <button type="button" className="sd-edit-avatar-remove" onClick={removeAvatar}>Remove</button>
                )}
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickAvatar} />
              </div>
            </div>

            <div className="sd-edit-grid">
              <label className="sd-edit-field sd-edit-field--full">
                <span>Full name</span>
                <input value={form.name} onChange={e => set('name', e.target.value)} />
              </label>
              <label className="sd-edit-field">
                <span>Role</span>
                <select value={form.role_category} onChange={e => set('role_category', e.target.value)}>
                  {EDIT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="sd-edit-field">
                <span>Job title</span>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Founding Partner" />
              </label>
              <label className="sd-edit-field">
                <span>Office</span>
                <select value={form.office} onChange={e => set('office', e.target.value)}>
                  {EDIT_OFFICES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
              <label className="sd-edit-field">
                <span>Birthday</span>
                <div className="sd-edit-bday">
                  <select value={form.birthMonth} onChange={e => set('birthMonth', e.target.value)}>
                    <option value="">Month</option>
                    {BIRTH_MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <input type="number" min="1" max="31" placeholder="Day" value={form.birthDay}
                    onChange={e => set('birthDay', e.target.value)} />
                </div>
              </label>
              <label className="sd-edit-field">
                <span>Office phone</span>
                <input value={form.phone} onChange={e => set('phone', e.target.value)} />
              </label>
              <label className="sd-edit-field">
                <span>Extension</span>
                <input value={form.phone_extension} onChange={e => set('phone_extension', e.target.value)} />
              </label>
              <label className="sd-edit-field">
                <span>Mobile phone</span>
                <input value={form.mobile_phone} onChange={e => set('mobile_phone', e.target.value)} />
              </label>
            </div>
            <div className="sd-edit-actions">
              <button className="sd-edit-cancel" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="sd-edit-save" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        ) : (
          <>
            <div className="sd-modal-top">
              <div className="sd-modal-avatar">
                {data.avatar
                  ? <img src={data.avatar} alt={data.name} />
                  : <span>{initials(data.name)}</span>}
              </div>
              <h2 className="sd-modal-name">{data.name}</h2>
              {data.title && <p className="sd-modal-title">{data.title}</p>}
              {data.office && <p className="sd-modal-location">{data.office}</p>}
              {data.todayStatus === 'out' && data.out_of_office_until && (
                <p className="sd-modal-status-return">
                  {`Back on ${new Date(String(data.out_of_office_until).substring(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}`}
                </p>
              )}
            </div>

            <div className="sd-modal-body">
              <a href={`mailto:${data.email}`} className="sd-modal-contact-row">
                <span className="sd-modal-contact-icon"><EmailIcon /></span>
                <span className="sd-modal-contact-text">{data.email}</span>
              </a>
              {office && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><PhoneIcon /></span>
                  <span className="sd-modal-contact-text">{office}</span>
                </div>
              )}
              {data.mobile_phone && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><MobileIcon /></span>
                  <span className="sd-modal-contact-text">{data.mobile_phone}</span>
                </div>
              )}
              {fmtBirthday(data.birthday) && (
                <div className="sd-modal-contact-row">
                  <span className="sd-modal-contact-icon"><CakeIcon /></span>
                  <span className="sd-modal-contact-text">{fmtBirthday(data.birthday)}</span>
                </div>
              )}

              {data.bio && (
                <>
                  <div className="sd-modal-divider" />
                  <p className="sd-modal-bio">{data.bio}</p>
                </>
              )}

              <a
                href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(data.email)}`}
                className="sd-modal-teams-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <TeamsIcon />
                Chat on Teams
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const StaffDirectory = ({ currentUser }) => {
  const isAdmin = currentUser?.isAdmin;
  const [staff,    setStaff]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [query,    setQuery]    = useState('');
  const fetchStaff = useCallback(async () => {
    try {
      const [staffRes, presenceRes] = await Promise.allSettled([getStaff(), getPresenceToday()]);
      if (staffRes.status !== 'fulfilled') return;
      const presenceMap = {};
      if (presenceRes.status === 'fulfilled') {
        (presenceRes.value.data || []).forEach(p => { presenceMap[String(p.id)] = p.status; });
      }
      const merged = staffRes.value.data.map(s => ({ ...s, todayStatus: presenceMap[String(s.id)] || null }));
      setStaff(merged);
    } catch (err) {
      console.error('Failed to load staff:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
    socket.on('staff:changed', fetchStaff);
    socket.on('presence:changed', fetchStaff);
    return () => {
      socket.off('staff:changed', fetchStaff);
      socket.off('presence:changed', fetchStaff);
    };
  }, [fetchStaff]);

  if (loading) return <p className="sd-loading">Loading directory…</p>;

  const grouped = { Attorneys: [], Staff: [] };
  staff.forEach(s => {
    if (ATTORNEY_ROLES.has(s.role_category)) grouped['Attorneys'].push(s);
    else grouped['Staff'].push(s);
  });

  const q = query.trim().toLowerCase();
  const filtered = q
    ? staff.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.title || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      )
    : null;

  return (
    <div className="sd-page">
      <h2 className="sd-page-title">Firm Directory</h2>

      <div className="sd-search-wrap">
        <input
          className="sd-search"
          type="text"
          placeholder="Search by name, title, or email…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query ? (
          <button className="sd-search-clear" onClick={() => setQuery('')}>✕</button>
        ) : (
          <span className="sd-search-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" />
              <line x1="16.5" y1="16.5" x2="22" y2="22" />
            </svg>
          </span>
        )}
      </div>

      {q && filtered.length === 0 && (
        <p className="sd-no-results">No staff match "{query}"</p>
      )}

      {q ? (
        <div className="sd-grid">
          {sortByLast(filtered).map(m => {
            const office = fmtPhone(m.phone, m.phone_extension);
            return (
              <div
                key={m.id}
                className="sd-card"
                onClick={() => setExpanded(m)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setExpanded(m)}
              >
                <div className="sd-avatar">
                  {m.avatar
                    ? <img src={m.avatar} alt={m.name} />
                    : <span>{initials(m.name)}</span>}
                </div>
                <div className="sd-info">
                  <div className="sd-name-row">
                    <p className="sd-name">{m.name}</p>
                    <a
                      href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(m.email)}`}
                      className="sd-teams-icon"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      title="Start Teams chat"
                    ><TeamsIcon /></a>
                  </div>
                  {m.title && <p className="sd-title">{m.title}</p>}
                  {m.office && <p className="sd-location">{m.office}</p>}
                  <div className="sd-contact-divider"/>
                  <span className="sd-email-row">
                    <span className="sd-contact-icon"><EmailIcon /></span>
                    <span className="sd-email-text">{m.email}</span>
                  </span>
                  {office && (
                    <span className="sd-contact-row">
                      <span className="sd-contact-icon"><PhoneIcon /></span>
                      <span className="sd-contact-text">{office}</span>
                    </span>
                  )}
                  {m.mobile_phone && (
                    <span className="sd-contact-row">
                      <span className="sd-contact-icon"><MobileIcon /></span>
                      <span className="sd-contact-text">{m.mobile_phone}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {!q && CATEGORIES.map(({ key, label }) => {
        const members = (key === 'Attorneys' ? sortAttorneys : sortByLast)(grouped[key] || []);
        if (!members.length) return null;
        return (
          <section key={key} className="sd-section">
            <div className="sd-section-header">
              <h3 className="sd-section-title">{label}</h3>
              <span className="sd-section-count">{members.length}</span>
            </div>
            <div className="sd-grid">
              {members.map(m => {
                const office = fmtPhone(m.phone, m.phone_extension);
                return (
                  <div
                    key={m.id}
                    className="sd-card"
                    onClick={() => setExpanded(m)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && setExpanded(m)}
                  >
                    <div className="sd-avatar">
                      {m.avatar
                        ? <img src={m.avatar} alt={m.name} />
                        : <span>{initials(m.name)}</span>}
                    </div>
                    <div className="sd-info">
                      <div className="sd-name-row">
                        <p className="sd-name">{m.name}</p>
                        <a
                          href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(m.email)}`}
                          className="sd-teams-icon"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          title="Start Teams chat"
                        ><TeamsIcon /></a>
                      </div>
                      {m.title && <p className="sd-title">{m.title}</p>}
                      {m.office && <p className="sd-location">{m.office}</p>}
                      <div className="sd-contact-divider"/>
                      <span className="sd-email-row">
                        <span className="sd-contact-icon"><EmailIcon /></span>
                        <span className="sd-email-text">{m.email}</span>
                      </span>
                      {office && (
                        <span className="sd-contact-row">
                          <span className="sd-contact-icon"><PhoneIcon /></span>
                          <span className="sd-contact-text">{office}</span>
                        </span>
                      )}
                      {m.mobile_phone && (
                        <span className="sd-contact-row">
                          <span className="sd-contact-icon"><MobileIcon /></span>
                          <span className="sd-contact-text">{m.mobile_phone}</span>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {expanded && (
        <ProfileModal
          member={expanded}
          isAdmin={isAdmin}
          onClose={() => setExpanded(null)}
          onSaved={fetchStaff}
        />
      )}
    </div>
  );
};

export default StaffDirectory;
